// src/components/AppsDownloadWidget.jsx
import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Center,
    Divider,
    Heading,
    HStack,
    IconButton,
    Image,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Stack,
    Text,
    useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";

const DEFAULT_APPS = [
    {
        key: "ios",
        name: "iOS App",
        subtitle: "Download on iPhone / iPad",
        // TODO: replace with your real App Store URL
        url: "https://apps.apple.com/us/app/eco-courier/id6744484449",
        // Random placeholder (replace with iOS icon image)
        iconImg: "/apple-logo.png",
        // Random placeholder (replace with your QR image)
        qrImg: "/qrapple.png",
        badge: { label: "iOS", colorScheme: "blue" },
    },
    {
        key: "android",
        name: "Android App",
        subtitle: "Download on Android devices",
        // TODO: replace with your real Play Store URL
        url: "http://thelabsys.com/couriers-app.apk",
        // Random placeholder (replace with Android icon image)
        iconImg: "/android-logo.png",
        // Random placeholder (replace with your QR image)
        qrImg: "qrandroid.png",
        badge: { label: "Android", colorScheme: "green" },
    },
];

function copyText(text) {
    try {
        navigator.clipboard?.writeText(text);
    } catch (_) { }
}

const AppsDownloadWidget = ({
    title = "📲 Download Courier App",
    description = "Click on the system you want to see the link + QR code.",
    apps = DEFAULT_APPS,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedKey, setSelectedKey] = useState(null);

    const selected = useMemo(
        () => apps.find((a) => a.key === selectedKey) || null,
        [apps, selectedKey]
    );

    const openApp = (key) => {
        setSelectedKey(key);
        onOpen();
    };

    const close = () => {
        onClose();
        // optional: keep selectedKey so reopen is instant
        // setSelectedKey(null);
    };

    return (
        <Box borderWidth="1px" borderRadius="lg" p={4} shadow="md">
            <HStack justify="space-between" align="start" mb={3} wrap="wrap" gap={2}>
                <Box>
                    <Heading size="md">{title}</Heading>
                    <Text mt={1} color="gray.600" fontSize="sm">
                        {description}
                    </Text>
                </Box>
            </HStack>

            <Divider my={3} />

            <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
                {apps.map((app) => (
                    <Box
                        key={app.key}
                        p={3}
                        borderWidth="1px"
                        borderRadius="lg"
                        shadow="sm"
                        cursor="pointer"
                        _hover={{ shadow: "md", borderColor: "blue.300" }}
                        onClick={() => openApp(app.key)}
                    >
                        <HStack spacing={4} align="center">
                            <Avatar
                                name={app.name}
                                src={app.iconImg}
                                size="md"
                                borderWidth="1px"
                                borderColor="gray.200"
                            />
                            <Box flex="1" minW={0}>
                                <HStack spacing={2} mb={1}>
                                    <Text fontWeight="bold" noOfLines={1}>
                                        {app.name}
                                    </Text>
                                    {app.badge ? (
                                        <Badge colorScheme={app.badge.colorScheme}>
                                            {app.badge.label}
                                        </Badge>
                                    ) : null}
                                </HStack>
                                <Text fontSize="sm" color="gray.600" noOfLines={1}>
                                    {app.subtitle}
                                </Text>
                                <Text fontSize="xs" color="gray.500" mt={1} noOfLines={1}>
                                    Tap to view QR + link
                                </Text>
                            </Box>

                            <IconButton
                                aria-label="Open"
                                icon={<ExternalLinkIcon />}
                                variant="ghost"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openApp(app.key);
                                }}
                            />
                        </HStack>
                    </Box>
                ))}
            </SimpleGrid>

            <Modal isOpen={isOpen} onClose={close} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {selected ? `Download: ${selected.name}` : "Download"}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={5}>
                        {!selected ? (
                            <Center py={10}>
                                <Text color="gray.500">No app selected.</Text>
                            </Center>
                        ) : (
                            <Stack spacing={4}>
                                <HStack spacing={3} align="center">
                                    <Avatar
                                        name={selected.name}
                                        src={selected.iconImg}
                                        size="md"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                    />
                                    <Box>
                                        <HStack spacing={2}>
                                            <Text fontWeight="bold">{selected.name}</Text>
                                            {selected.badge ? (
                                                <Badge colorScheme={selected.badge.colorScheme}>
                                                    {selected.badge.label}
                                                </Badge>
                                            ) : null}
                                        </HStack>
                                        <Text fontSize="sm" color="gray.600">
                                            {selected.subtitle}
                                        </Text>
                                    </Box>
                                </HStack>

                                <Divider />

                                <Box>
                                    <Text fontSize="sm" color="gray.600" mb={2}>
                                        Link
                                    </Text>

                                    <Box
                                        borderWidth="1px"
                                        borderRadius="md"
                                        p={3}
                                        bg="gray.50"
                                        wordBreak="break-all"
                                    >
                                        <Link href={selected.url} isExternal fontWeight="semibold">
                                            {selected.url} <ExternalLinkIcon mx="2px" />
                                        </Link>

                                        <HStack mt={3} spacing={2} justify="flex-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => copyText(selected.url)}
                                            >
                                                Copy URL
                                            </Button>
                                            <Button
                                                size="sm"
                                                colorScheme="blue"
                                                as={Link}
                                                href={selected.url}
                                                isExternal
                                            >
                                                Open
                                            </Button>
                                        </HStack>
                                    </Box>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="gray.600" mb={2}>
                                        QR Code
                                    </Text>
                                    <Center>
                                        <Box
                                            borderWidth="1px"
                                            borderRadius="lg"
                                            p={3}
                                            bg="white"
                                            shadow="sm"
                                        >
                                            <Image
                                                src={selected.qrImg}
                                                alt={`${selected.name} QR`}
                                                boxSize={{ base: "240px", md: "280px" }}
                                                objectFit="cover"
                                                borderRadius="md"
                                            />
                                        </Box>
                                    </Center>
                                    <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
                                        Scan with your camera to open the link.
                                    </Text>
                                </Box>
                            </Stack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AppsDownloadWidget;